import { Fragment, type ReactNode } from 'react';

import { isSafeHref } from '@/lib/richtext/safeHref';

/**
 * Read-only renderer for TipTap-shaped JSON documents.
 *
 * Moved out of `components/compliance/SopRenderer` when the training LMS
 * landed — SOPs and training lessons share the exact same document shape,
 * so they share one renderer. Walking the JSON into JSX (instead of
 * mounting the TipTap editor read-only) lets us:
 *
 * - Skip a ~150 KB editor dependency for what's effectively a static page.
 * - Apply our own typography styles (kw-* tokens) without fighting TipTap's
 *   default chrome.
 * - Produce print-friendly HTML naturally (no contentEditable, no toolbars).
 *
 * Supported node types match what `server/src/lib/compliance/docxToTipTap.ts`
 * emits (and what the StarterKit-only RichTextEditor produces): doc /
 * paragraph / heading / bulletList / orderedList / listItem / blockquote /
 * horizontalRule / table / tableRow / tableHeader / tableCell / text (with
 * bold/italic/underline/link marks) / hardBreak.
 *
 * Unknown nodes fall through to a `<div>` wrapping their text — we never
 * silently drop content, which matters for cGMP defensibility.
 */

export interface TipTapDoc {
  type: 'doc';
  content?: TipTapNode[];
}

export interface TipTapNode {
  type: string;
  attrs?: Record<string, unknown>;
  content?: TipTapNode[];
  marks?: { type: string; attrs?: Record<string, unknown> }[];
  text?: string;
}

export function TipTapRenderer({
  doc,
  emptyText = 'No content yet.',
}: {
  doc: TipTapDoc | null | undefined;
  emptyText?: string;
}) {
  if (!doc?.content || doc.content.length === 0) {
    return (
      <div className="rounded-kw-md bg-kw-bg p-6 text-center text-[13px] text-kw-muted">{emptyText}</div>
    );
  }
  return <div className="sop-doc">{doc.content.map((node, i) => renderNode(node, i))}</div>;
}

function renderNode(node: TipTapNode, key: number): ReactNode {
  switch (node.type) {
    case 'paragraph':
      return (
        <p key={key} className="my-3 text-[14px] leading-relaxed text-kw-shark">
          {renderInline(node.content)}
        </p>
      );
    case 'heading': {
      const level = clampHeadingLevel(node.attrs?.level);
      const Tag = `h${String(level)}` as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
      return (
        <Tag key={key} className={headingClass(level)}>
          {renderInline(node.content)}
        </Tag>
      );
    }
    case 'bulletList':
      return (
        <ul key={key} className="my-3 list-disc space-y-1 pl-6 text-[14px] text-kw-shark">
          {node.content?.map((c, i) => renderNode(c, i))}
        </ul>
      );
    case 'orderedList':
      return (
        <ol key={key} className="my-3 list-decimal space-y-1 pl-6 text-[14px] text-kw-shark">
          {node.content?.map((c, i) => renderNode(c, i))}
        </ol>
      );
    case 'listItem':
      return <li key={key}>{node.content?.map((c, i) => renderNode(c, i))}</li>;
    case 'blockquote':
      return (
        <blockquote
          key={key}
          className="my-4 border-l-4 border-kw-cerulean/40 bg-kw-bg-blue px-4 py-2 italic text-kw-shark"
        >
          {node.content?.map((c, i) => renderNode(c, i))}
        </blockquote>
      );
    case 'horizontalRule':
      return <hr key={key} className="my-6 border-kw-border" />;
    case 'table':
      return (
        <div key={key} className="my-4 overflow-x-auto">
          <table className="min-w-full border-collapse border border-kw-border text-[13px]">
            <tbody>{node.content?.map((row, i) => renderNode(row, i))}</tbody>
          </table>
        </div>
      );
    case 'tableRow':
      return <tr key={key}>{node.content?.map((c, i) => renderNode(c, i))}</tr>;
    case 'tableHeader':
      return (
        <th key={key} className="border border-kw-border bg-kw-bg p-2 text-left font-semibold">
          {node.content?.map((c, i) => renderNode(c, i))}
        </th>
      );
    case 'tableCell':
      return (
        <td key={key} className="border border-kw-border p-2 align-top">
          {node.content?.map((c, i) => renderNode(c, i))}
        </td>
      );
    case 'hardBreak':
      return <br key={key} />;
    case 'text':
      return renderText(node, key);
    default:
      // Unknown node — render text content if any so we never silently drop.
      return node.text ? (
        <span key={key} className="text-kw-muted">
          {node.text}
        </span>
      ) : (
        <div key={key} className="my-2 rounded bg-kw-warning-bg p-2 text-[12px] text-kw-warning-text">
          Unsupported block type: <code>{node.type}</code>
        </div>
      );
  }
}

function renderInline(nodes: TipTapNode[] | undefined): ReactNode {
  if (!nodes || nodes.length === 0) return null;
  return nodes.map((n, i) => {
    if (n.type === 'hardBreak') return <br key={i} />;
    if (n.type !== 'text') return renderNode(n, i);
    return renderText(n, i);
  });
}

function renderText(node: TipTapNode, key: number): ReactNode {
  let element: ReactNode = node.text ?? '';
  for (const mark of node.marks ?? []) {
    if (mark.type === 'bold') element = <strong>{element}</strong>;
    else if (mark.type === 'italic') element = <em>{element}</em>;
    else if (mark.type === 'underline') element = <u>{element}</u>;
    else if (mark.type === 'link') {
      const rawHref = typeof mark.attrs?.href === 'string' ? mark.attrs.href : '';
      // Defense-in-depth against a `javascript:`/`data:` href smuggled via an
      // imported .docx (ADR-037): only render a live anchor for an allow-listed
      // scheme; otherwise keep the (still-styled) text without the link.
      element = isSafeHref(rawHref) ? (
        <a
          href={rawHref}
          className="text-kw-cerulean underline hover:text-kw-cerulean-dark"
          target="_blank"
          rel="noreferrer"
        >
          {element}
        </a>
      ) : (
        element
      );
    }
  }
  return <Fragment key={key}>{element}</Fragment>;
}

function clampHeadingLevel(raw: unknown): 1 | 2 | 3 | 4 | 5 | 6 {
  let n: number;
  if (typeof raw === 'number') n = raw;
  else if (typeof raw === 'string') n = Number.parseInt(raw, 10);
  else return 2;
  if (Number.isFinite(n) && n >= 1 && n <= 6) return n as 1 | 2 | 3 | 4 | 5 | 6;
  return 2;
}

function headingClass(level: number): string {
  switch (level) {
    case 1:
      return 'mt-6 mb-3 text-[24px] font-bold text-kw-shark';
    case 2:
      return 'mt-5 mb-2 text-[20px] font-semibold text-kw-shark';
    case 3:
      return 'mt-4 mb-2 text-[17px] font-semibold text-kw-shark';
    case 4:
      return 'mt-4 mb-2 text-[15px] font-semibold text-kw-shark';
    case 5:
      return 'mt-3 mb-1 text-[14px] font-semibold text-kw-shark';
    default:
      return 'mt-3 mb-1 text-[13px] font-semibold uppercase tracking-wide text-kw-muted';
  }
}
